import { AuthBaseClient } from '../base-client';
import type {
  SimulateRequest,
  AppointmentTypeRecommendation,
  AppointmentTypePreferenceResponse,
  RecordPreferenceRequest,
  SimulateForTaskResponse,
  SimulateBatchResponse,
} from './types';

/**
 * Client for appointment type matching and preference memory API endpoints.
 */
export class AppointmentTypeMatchingClient extends AuthBaseClient {
  /**
   * Simulate appointment type selection for a given set of referral signals.
   * Returns the recommended type, confidence level, score breakdown, and
   * any applied preference memory.
   */
  async simulate(request: SimulateRequest): Promise<AppointmentTypeRecommendation> {
    return this.post<AppointmentTypeRecommendation>('/api/appointment-types/simulate', request);
  }

  /**
   * Simulate the appointment type for a referral TASK. The server resolves
   * the task's linked referral document, derives the referral signals from
   * it, and runs the matcher. Returns whether a document was found, the
   * derived signals, and the recommendation. Used by the Tasks Overview to
   * preview which appointment type a referral would land on.
   */
  async simulateForTask(taskId: string): Promise<SimulateForTaskResponse> {
    return this.post<SimulateForTaskResponse>('/api/appointment-types/simulate-for-task', {
      taskId,
    });
  }

  /**
   * Rank the provider pool for a page of referral tasks in ONE request.
   * Powers the Referrals worklist's "Provider(s)" column — see
   * `simulateBatchRequestSchema` for why this isn't N `simulateForTask` calls.
   */
  async simulateProvidersForTasks(taskIds: string[]): Promise<SimulateBatchResponse> {
    return this.post<SimulateBatchResponse>('/api/appointment-types/simulate-batch', { taskIds });
  }

  /**
   * List all saved tie-break preferences for the organization.
   */
  async listPreferences(): Promise<AppointmentTypePreferenceResponse[]> {
    return this.get<AppointmentTypePreferenceResponse[]>('/api/appointment-type-preferences');
  }

  /**
   * Save a new preference after a human tie-break decision.
   */
  async recordPreference(
    data: RecordPreferenceRequest
  ): Promise<AppointmentTypePreferenceResponse> {
    return this.post<AppointmentTypePreferenceResponse>('/api/appointment-type-preferences', data);
  }

  /**
   * Delete (soft-delete) a preference.
   */
  async deletePreference(id: string): Promise<void> {
    await this.delete(`/api/appointment-type-preferences/${id}`);
  }
}
